import { FC, useEffect, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, Typography, SvgIcon } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { client, MarketCatalogue, Instance, Market } from '../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../components/forms/Pagination';
import { Section } from '../../../components/ui/Section';
import CataloguesList from './CataloguesList';

type Props = {
  onCreateNew: () => void;
};

const CataloguesTab: FC<Props> = ({ onCreateNew }) => {
  const { t } = useTranslation('catalogues');
  const [list, setList] = useState<MarketCatalogue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const pageSize = 20;

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchMarkets = async () => {
    try {
      const result = await client.getMarkets({
        companyId: selectedInstance || undefined,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setMarkets(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const fetchCatalogues = async () => {
    setIsLoading(true);
    try {
      const result = await client.getMarketCatalogues({
        companyId: selectedInstance || undefined,
        customerTypeId: selectedMarket || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
      });

      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      }
    } catch (error) {
      console.error('Error fetching catalogues:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
  }, []);

  useEffect(() => {
    if (selectedInstance) {
      fetchMarkets();
    } else {
      setMarkets([]);
      setSelectedMarket('');
    }
  }, [selectedInstance]);

  useEffect(() => {
    fetchCatalogues();
  }, [currentPage, searchTerm, selectedInstance, selectedMarket]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket(''); // Reset market when instance changes
    setCurrentPage(0);
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
    setCurrentPage(0);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('');
    setSelectedMarket('');
    setCurrentPage(0);
  };

  return (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('management.search')}
          placeholder={t('management.searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          <Typography>{t('management.clear')}</Typography>
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          <Typography>{t('management.search')}</Typography>
        </Button>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('management.filterByInstance')}
          </InputLabel>
          <Select
            value={selectedInstance}
            onChange={handleInstanceChange}
            label={t('management.filterByInstance')}
            displayEmpty
            notched
          >
            <MenuItem value="">
              {t('management.allInstances')}
            </MenuItem>
            {instances.map((instance) => (
              <MenuItem 
                key={instance.id} 
                value={instance.id.toString()}
              >
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('management.filterByMarket')}
          </InputLabel>
          <Select
            value={selectedMarket}
            onChange={handleMarketChange}
            label={t('management.filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            <MenuItem value="">
              {t('management.allMarkets')}
            </MenuItem>
            {markets.map((market) => (
              <MenuItem 
                key={market.customerTypeId} 
                value={market.customerTypeId.toString()}
              >
                {market.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <CataloguesList
            list={list}
            isLoading={isLoading}
            totalCount={totalCount}
            onClear={handleClear}
            onCreateNew={onCreateNew}
          />
          {totalCount > pageSize && (
            <CustomPagination
              page={currentPage}
              setPage={handlePageChange}
              maxPages={Math.ceil(totalCount / pageSize)}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          )}
        </Section>
      </Grid>
    </Grid>
  );
};

export default CataloguesTab;
